import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import AgentCriteriaEditor, { ZoneValue } from '../../../components/LeadsVo/AgentCriteriaEditor';
import RdvStatusBadge from '../../../components/LeadsVo/RdvStatusBadge';
import {
  useAdminGiftPack,
  useAdminLeadsVoAgentDetail,
  useAdminSendPackPaymentLink,
  useAdminUpdateAgentBusinessHours,
  useAdminUpdateAgentResearch,
  useAdminUpdateAgentZone,
} from '../../../hooks/useLeadsVoQueries';
import { LeadsVoPartnerRdv } from '../../../types/leadsVo';
import { hasPuretechExcluded } from '../../../utils/leadsVoMotorisations';

const headerStyle = {
  background: '#262626',
  color: 'white',
  fontWeight: 'bold',
  fontSize: 14,
  position: 'sticky' as const,
  top: 0,
  zIndex: 100,
  whiteSpace: 'nowrap' as const,
};

const cellStyle = {
  height: 55,
  verticalAlign: 'middle' as const,
  fontSize: 14,
  color: '#262626',
};

// Prix unitaire HT d'un RDV — miroir du backend (LEADS_VO_RDV_UNIT_PRICE_HT, défaut 35€).
// Sert uniquement à l'estimation affichée ; le backend reste la source de vérité.
const UNIT_PRICE_HT = 35;

const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  onboarding: { bg: '#FFF3E0', color: '#E65100' },
  active: { bg: '#E8F5E9', color: '#2E7D32' },
  paused: { bg: '#FFF8E1', color: '#F57F17' },
  inactive: { bg: '#FFEBEE', color: '#C62828' },
};

// L'URL d'annonce vient d'un service externe : on ne rend le lien que pour
// les schémas http(s) (bloque les URI javascript: et autres).
const safeHttpUrl = (url: string | undefined): string | undefined =>
  url && /^https?:\/\//i.test(url) ? url : undefined;

const RDV_LOCATION_LABELS: Record<string, string> = {
  agency: 'En agence',
  home: 'À domicile',
  flexible: 'Flexible',
};

const DAYS: { key: string; label: string }[] = [
  { key: 'lundi', label: 'Lundi' },
  { key: 'mardi', label: 'Mardi' },
  { key: 'mercredi', label: 'Mercredi' },
  { key: 'jeudi', label: 'Jeudi' },
  { key: 'vendredi', label: 'Vendredi' },
  { key: 'samedi', label: 'Samedi' },
  { key: 'dimanche', label: 'Dimanche' },
];

const SectionCard = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Paper elevation={3} sx={{ p: 3, borderRadius: 2, height: '100%' }}>
    <Typography sx={{ fontWeight: 700, fontSize: 16, color: '#262626', mb: 2 }}>{title}</Typography>
    {children}
  </Paper>
);

const InfoLine = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ py: 0.5 }}>
    <Typography sx={{ fontSize: 14, color: '#464646' }}>{label}</Typography>
    <Typography component="div" sx={{ fontSize: 14, color: '#262626', fontWeight: 600, textAlign: 'right' }}>
      {value}
    </Typography>
  </Box>
);

const ChipList = ({ label, values }: { label: string; values: string[] | undefined }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography sx={{ fontSize: 13, color: '#464646', fontWeight: 600, mb: 0.5 }}>{label}</Typography>
    {values && values.length > 0 ? (
      <Box display="flex" flexWrap="wrap" gap={0.5}>
        {values.map((v) => (
          <Chip key={v} label={v} size="small" sx={{ fontSize: 12, backgroundColor: '#F5F5F5', color: '#262626' }} />
        ))}
      </Box>
    ) : (
      <Typography sx={{ fontSize: 13, color: '#9E9E9E' }}>Aucune restriction</Typography>
    )}
  </Box>
);

const formatRange = (min: number | null | undefined, max: number | null | undefined, unit: string): string => {
  // Pas de séparateur de milliers sans unité (cas des années : « 2018 », pas « 2 018 »).
  const fmt = (n: number): string => (unit ? n.toLocaleString('fr-FR') : String(n));
  const suffix = unit ? ` ${unit}` : '';
  if (min == null && max == null) return 'Sans limite';
  if (min != null && max != null) return `${fmt(min)} – ${fmt(max)}${suffix}`;
  if (min != null) return `≥ ${fmt(min)}${suffix}`;
  return `≤ ${fmt(max!)}${suffix}`;
};

const StatCard = ({
  title,
  value,
  subtitle,
  accent = false,
}: {
  title: string;
  value: number;
  subtitle?: string;
  accent?: boolean;
}) => (
  <Paper
    elevation={3}
    sx={{
      p: 2,
      borderRadius: 2,
      borderLeft: accent ? '4px solid #FDBB5F' : '4px solid transparent',
      height: '100%',
    }}
  >
    <Typography sx={{ color: '#464646', fontWeight: 500, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>
      {title}
    </Typography>
    <Typography sx={{ color: '#262626', fontWeight: 900, fontSize: 26, lineHeight: 1.3 }}>{value}</Typography>
    {subtitle && <Typography sx={{ color: '#9E9E9E', fontSize: 12 }}>{subtitle}</Typography>}
  </Paper>
);

const StatsBlockHeader = ({ title, hint }: { title: string; hint: string }) => (
  <Box display="flex" alignItems="baseline" gap={1.5} mb={1.5} flexWrap="wrap">
    <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#262626', textTransform: 'uppercase', letterSpacing: 0.5 }}>
      {title}
    </Typography>
    <Typography sx={{ fontSize: 13, color: '#9E9E9E' }}>{hint}</Typography>
  </Box>
);

const AdminAgentDetail = () => {
  const { id } = useParams();
  const agentId = Number(id);

  const { data: agent, isLoading } = useAdminLeadsVoAgentDetail(agentId);
  const adminUpdateZone = useAdminUpdateAgentZone(agentId);
  const adminUpdateResearch = useAdminUpdateAgentResearch(agentId);
  const adminUpdateHours = useAdminUpdateAgentBusinessHours(agentId);
  const sendPaymentLink = useAdminSendPackPaymentLink(agentId);
  const giftPack = useAdminGiftPack(agentId);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [editing, setEditing] = useState(false);

  // Dialog « Envoyer un lien de paiement »
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkSize, setLinkSize] = useState('10');
  const [linkMethod, setLinkMethod] = useState<'stripe_card' | 'qonto_transfer'>('stripe_card');
  const [linkError, setLinkError] = useState('');
  const [toast, setToast] = useState('');

  // Dialog « Offrir des RDV »
  const [giftOpen, setGiftOpen] = useState(false);
  const [giftSize, setGiftSize] = useState('5');
  const [giftReason, setGiftReason] = useState('');
  const [giftError, setGiftError] = useState('');

  const submitGift = async () => {
    const size = Number(giftSize);
    if (!Number.isInteger(size) || size < 1) {
      setGiftError('Indique un nombre de RDV valide (entier ≥ 1).');
      return;
    }
    if (!giftReason.trim()) {
      setGiftError('Indique un motif : il est conservé dans l\'historique admin.');
      return;
    }
    setGiftError('');
    try {
      const res: any = await giftPack.mutateAsync({ size, reason: giftReason.trim() });
      setGiftOpen(false);
      setGiftReason('');
      const delivery = res?.provisioned
        ? 'crédités chez le partenaire'
        : 'enregistrés, activation chez le partenaire sous 15 min';
      const mail = res?.sent_to ? `, email envoyé à ${res.sent_to}` : ', aucun email (adresse agence inconnue)';
      setToast(`${size} RDV offerts ${delivery}${mail}.`);
    } catch (err: any) {
      setGiftError(err?.response?.data?.message ?? 'Échec du don de RDV.');
    }
  };

  const submitPaymentLink = async () => {
    const size = Number(linkSize);
    if (!Number.isInteger(size) || size < 1) {
      setLinkError('Indique un nombre de RDV valide (entier ≥ 1).');
      return;
    }
    setLinkError('');
    try {
      const res: any = await sendPaymentLink.mutateAsync({ size, payment_method: linkMethod });
      setLinkOpen(false);
      setToast(`Email envoyé à ${res?.sent_to ?? "l'agence"}.`);
    } catch (err: any) {
      setLinkError(err?.response?.data?.message ?? "Échec de l'envoi du lien de paiement.");
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress sx={{ color: '#FDBB5F' }} />
      </Box>
    );
  }

  if (!agent) {
    return (
      <Container maxWidth={false} sx={{ py: 4, px: 4 }}>
        <Typography sx={{ color: '#464646' }}>Agent introuvable.</Typography>
      </Container>
    );
  }

  const statusColor = STATUS_COLORS[agent.status] ?? { bg: '#F5F5F5', color: '#616161' };
  const partner = agent.partner;
  const criteria = agent.research?.criteres_vehicule ?? (agent.research_config as any)?.criteres_vehicule;
  const stats = agent.stats;
  const rdvs = agent.rdvs ?? [];
  const pagedRdvs = rdvs.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  // Zone : données partenaire en priorité, config locale en secours.
  const zone = partner
    ? { mode: partner.zone_mode, cities: partner.zone_cities, center: partner.zone_center, radius_km: partner.zone_radius_km }
    : (agent.zone_config as any);

  const businessHours = partner?.business_hours;

  return (
    <Container maxWidth={false} sx={{ py: 4, px: 4 }}>
      <Box mb={1}>
        <Link to="/leads-vo/admin/agents" style={{ color: '#464646', fontSize: 14, textDecoration: 'none' }}>
          ← Retour aux agents
        </Link>
      </Box>

      <Box display="flex" alignItems="center" gap={2} mb={3} flexWrap="wrap">
        <Typography sx={{ fontWeight: 900, fontSize: 28, color: '#262626' }}>
          {agent.organization_name ?? `Organisation #${agent.organization_id}`}
        </Typography>
        {agent.leads_vo_agent_id && (
          <Button
            onClick={() => setEditing(!editing)}
            sx={{
              ml: 'auto',
              backgroundColor: editing ? '#F5F5F5' : '#FDBB5F',
              color: '#262626',
              textTransform: 'none',
              fontWeight: 600,
              px: 2.5,
              '&:hover': { backgroundColor: editing ? '#EEEEEE' : '#fcae4d' },
            }}
          >
            {editing ? "Terminer l'édition" : 'Modifier les paramètres'}
          </Button>
        )}
        {agent.leads_vo_agent_id && (
          <Button
            onClick={() => {
              setLinkError('');
              setLinkOpen(true);
            }}
            sx={{
              ml: agent.leads_vo_agent_id ? 0 : 'auto',
              backgroundColor: '#262626',
              color: 'white',
              textTransform: 'none',
              fontWeight: 600,
              px: 2.5,
              '&:hover': { backgroundColor: '#000' },
            }}
          >
            Envoyer un lien de paiement
          </Button>
        )}
        {agent.leads_vo_agent_id && (
          <Button
            onClick={() => {
              setGiftError('');
              setGiftOpen(true);
            }}
            sx={{
              backgroundColor: '#E8F5E9',
              color: '#1B5E20',
              textTransform: 'none',
              fontWeight: 600,
              px: 2.5,
              '&:hover': { backgroundColor: '#DCEDC8' },
            }}
          >
            Offrir des RDV
          </Button>
        )}
        <Chip
          label={agent.status}
          size="small"
          sx={{ backgroundColor: statusColor.bg, color: statusColor.color, fontWeight: 600, fontSize: 12 }}
        />
        <Chip
          label={agent.google_calendar_connected ? 'Calendrier connecté' : 'Calendrier non connecté'}
          size="small"
          variant={agent.google_calendar_connected ? 'filled' : 'outlined'}
          sx={{
            fontSize: 12,
            ...(agent.google_calendar_connected
              ? { backgroundColor: '#E8F5E9', color: '#2E7D32' }
              : { borderColor: '#BDBDBD', color: '#757575' }),
          }}
        />
        <Chip
          label={agent.leads_vo_enabled ? 'RDV Qualifiés activé' : 'RDV Qualifiés désactivé'}
          size="small"
          sx={{
            fontSize: 12,
            ...(agent.leads_vo_enabled
              ? { backgroundColor: '#FDBB5F', color: '#262626', fontWeight: 600 }
              : { backgroundColor: '#FFEBEE', color: '#C62828' }),
          }}
        />
      </Box>

      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={4}>
          <SectionCard title="Informations">
            <InfoLine label="Contact" value={partner ? `${partner.first_name ?? ''} ${partner.last_name ?? ''}`.trim() || '—' : '—'} />
            <InfoLine label="Email" value={partner?.email ?? '—'} />
            <InfoLine label="Téléphone" value={partner?.telephone_mobile ?? '—'} />
            <InfoLine label="Lieu des RDV" value={partner?.rdv_location ? RDV_LOCATION_LABELS[partner.rdv_location] ?? partner.rdv_location : '—'} />
            <InfoLine label="Adresse agence" value={partner?.agency_address ?? '—'} />
            <InfoLine label="Inscrit le" value={new Date(agent.created_at).toLocaleDateString('fr-FR')} />
          </SectionCard>
        </Grid>

        {/* Lecture seule par défaut ; le bouton « Modifier » bascule en édition. */}
        {!editing && (
          <>
            <Grid item xs={12} md={4}>
              <SectionCard title="Zone de chalandise">
                {zone?.mode === 'cities' && (
                  <ChipList label={`Villes (${zone.cities?.length ?? 0})`} values={zone.cities} />
                )}
                {zone?.mode === 'radius' && (
                  <>
                    <InfoLine label="Ville centre" value={zone.center ?? '—'} />
                    <InfoLine label="Rayon" value={zone.radius_km != null ? `${zone.radius_km} km` : '—'} />
                  </>
                )}
                {!zone?.mode && <Typography sx={{ fontSize: 13, color: '#9E9E9E' }}>Zone non configurée</Typography>}
              </SectionCard>
            </Grid>

            <Grid item xs={12} md={4}>
              <SectionCard title="Horaires de RDV">
                {businessHours && Object.keys(businessHours).length > 0 ? (
                  DAYS.filter((d) => (businessHours[d.key] ?? []).length > 0).map((d) => (
                    <InfoLine
                      key={d.key}
                      label={d.label}
                      value={(businessHours[d.key] ?? []).map((slot) => `${slot.start} – ${slot.end}`).join(' / ')}
                    />
                  ))
                ) : (
                  <Typography sx={{ fontSize: 13, color: '#9E9E9E' }}>Horaires non configurés</Typography>
                )}
              </SectionCard>
            </Grid>

            <Grid item xs={12}>
              <SectionCard title="Critères de recherche">
                {criteria ? (
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <ChipList label={`Marques (${criteria.marques_voulues?.length ?? 0})`} values={criteria.marques_voulues} />
                      <ChipList label="Carburants" values={criteria.carburants} />
                      <ChipList label="Boîtes de vitesses" values={criteria.boites} />
                      <ChipList label="Carrosseries" values={criteria.types_carrosserie} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <InfoLine label="Prix" value={formatRange(criteria.prix_min_eur, criteria.prix_max_eur, '€')} />
                      <InfoLine label="Année" value={formatRange(criteria.annee_min, criteria.annee_max, '')} />
                      <InfoLine label="Kilométrage" value={formatRange(criteria.km_min, criteria.km_max, 'km')} />
                      <InfoLine label="Véhicules accidentés" value={criteria.exclure_accidentes ? 'Exclus' : 'Acceptés'} />
                      <InfoLine label="Véhicules d'import" value={criteria.exclure_import ? 'Exclus' : 'Acceptés'} />
                      <InfoLine label="Moteurs PureTech" value={hasPuretechExcluded(criteria.motorisations_exclues) ? 'Exclus' : 'Acceptés'} />
                    </Grid>
                  </Grid>
                ) : (
                  <Typography sx={{ fontSize: 13, color: '#9E9E9E' }}>Critères non configurés</Typography>
                )}
              </SectionCard>
            </Grid>
          </>
        )}
      </Grid>

      {/* Mode édition : remplace les cartes lecture seule par les éditeurs. */}
      {editing && agent.leads_vo_agent_id && (
        <>
          <Typography sx={{ fontWeight: 700, fontSize: 20, color: '#262626', mb: 2 }}>
            Modifier les paramètres
          </Typography>
          <AgentCriteriaEditor
            initialZone={zone?.mode ? (zone as ZoneValue) : null}
            initialResearch={criteria ?? null}
            initialHours={businessHours ?? null}
            onSaveZone={(payload) => adminUpdateZone.mutateAsync(payload)}
            onSaveResearch={(payload) => adminUpdateResearch.mutateAsync(payload)}
            onSaveHours={(hours) => adminUpdateHours.mutateAsync(hours)}
          />
        </>
      )}

      {stats && (
        <>
          <Typography sx={{ fontWeight: 700, fontSize: 20, color: '#262626', mb: 2 }}>Statistiques RDV</Typography>

          <StatsBlockHeader title="Crédits pack" hint="facturation partenaire — no-shows et annulations re-crédités" />
          <Grid container spacing={2} mb={3}>
            <Grid item xs={6} sm={4} md={2}>
              <StatCard title="Achetés" value={stats.rdvs_purchased} accent />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <StatCard title="Consommés" value={stats.rdvs_consumed} />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <StatCard title="Restants" value={stats.rdvs_remaining} accent />
            </Grid>
          </Grid>

          <StatsBlockHeader title="Activité RDV" hint="tous les RDV livrés, y compris re-crédités" />
          <Grid container spacing={2} mb={3}>
            <Grid item xs={6} sm={4} md={2}>
              <StatCard title="À venir" value={stats.rdvs_upcoming} />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <StatCard title="Complétés" value={stats.rdvs_completed} accent />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <StatCard
                title="No-show"
                value={stats.rdvs_no_show + stats.rdvs_no_show_pending}
                subtitle={stats.rdvs_no_show_pending > 0 ? `dont ${stats.rdvs_no_show_pending} à valider` : undefined}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <StatCard
                title="Annulés"
                value={stats.rdvs_cancelled_by_agent + stats.rdvs_cancelled_by_client}
                subtitle={`agence ${stats.rdvs_cancelled_by_agent} · particulier ${stats.rdvs_cancelled_by_client}`}
              />
            </Grid>
          </Grid>
        </>
      )}

      <Typography sx={{ fontWeight: 700, fontSize: 20, color: '#262626', mb: 2 }}>
        Rendez-vous ({rdvs.length})
      </Typography>

      {agent.rdvs_available === false && (
        <Typography sx={{ color: '#C62828', fontSize: 14, mb: 2 }}>
          Les rendez-vous n'ont pas pu être récupérés auprès du partenaire — réessayez plus tard.
        </Typography>
      )}

      <Paper elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headerStyle}>Date</TableCell>
                <TableCell sx={headerStyle}>Lead</TableCell>
                <TableCell sx={headerStyle}>Véhicule</TableCell>
                <TableCell sx={headerStyle}>Téléphone</TableCell>
                <TableCell sx={headerStyle}>Statut</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedRdvs.length > 0 ? (
                pagedRdvs.map((rdv: LeadsVoPartnerRdv) => {
                  // Shape partenaire en priorité, fallback champs locaux (cf. Rdvs.tsx agence).
                  const nom = rdv.particulier?.nom ?? rdv.lead_name ?? '—';
                  const tel = rdv.particulier?.telephone ?? rdv.particulier?.phone ?? rdv.lead_phone ?? '—';
                  const v = rdv.vehicule;
                  const vehicule = v ? `${v.marque ?? ''} ${v.modele ?? ''} ${v.annee ?? ''}`.trim() : rdv.lead_vehicle ?? '—';
                  const date = rdv.slot?.start ?? rdv.slot_start ?? rdv.scheduled_at;
                  const statut = rdv.statut ?? rdv.status ?? '—';
                  const adUrl = safeHttpUrl(rdv.ad_url ?? rdv.lead_source_url);

                  return (
                    <TableRow key={rdv.id} hover sx={{ '&:hover': { backgroundColor: '#FAFAFA' } }}>
                      <TableCell sx={cellStyle}>
                        {date
                          ? new Date(date).toLocaleDateString('fr-FR', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })
                          : '—'}
                      </TableCell>
                      <TableCell sx={{ ...cellStyle, fontWeight: 600 }}>{nom}</TableCell>
                      <TableCell sx={cellStyle}>
                        {adUrl ? (
                          <a
                            href={adUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#262626', textDecoration: 'underline', textUnderlineOffset: 3 }}
                          >
                            {vehicule} ↗
                          </a>
                        ) : (
                          vehicule
                        )}
                      </TableCell>
                      <TableCell sx={cellStyle}>{tel}</TableCell>
                      <TableCell sx={cellStyle}>
                        <RdvStatusBadge status={statut} />
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={5} sx={{ textAlign: 'center', py: 6, color: '#464646' }}>
                    Aucun rendez-vous pour cette agence
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={rdvs.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          labelRowsPerPage="Lignes par page"
          sx={{ borderTop: '1px solid #E0E0E0' }}
        />
      </Paper>

      <Dialog open={linkOpen} onClose={() => setLinkOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Envoyer un lien de paiement</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14, color: '#464646', mb: 2 }}>
            Un pack sera créé et un email de paiement envoyé à l'agence
            {agent.partner?.email ? ` (${agent.partner.email})` : ''}.
          </Typography>
          <TextField
            label="Nombre de RDV"
            type="number"
            value={linkSize}
            onChange={(e) => setLinkSize(e.target.value)}
            fullWidth
            inputProps={{ min: 1, step: 1 }}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Moyen de paiement"
            select
            value={linkMethod}
            onChange={(e) => setLinkMethod(e.target.value as 'stripe_card' | 'qonto_transfer')}
            fullWidth
          >
            <MenuItem value="stripe_card">Carte bancaire (lien Stripe)</MenuItem>
            <MenuItem value="qonto_transfer">Virement (facture Qonto)</MenuItem>
          </TextField>
          <Typography sx={{ fontSize: 13, color: '#9E9E9E', mt: 2 }}>
            Total estimé : {(Number(linkSize) * UNIT_PRICE_HT || 0).toLocaleString('fr-FR')} € HT —{' '}
            {(Number(linkSize) * UNIT_PRICE_HT * 1.2 || 0).toLocaleString('fr-FR')} € TTC
          </Typography>
          {linkError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {linkError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setLinkOpen(false)} sx={{ color: '#464646', textTransform: 'none' }}>
            Annuler
          </Button>
          <Button
            onClick={submitPaymentLink}
            disabled={sendPaymentLink.isPending}
            sx={{ backgroundColor: '#FDBB5F', color: '#262626', textTransform: 'none', fontWeight: 700, px: 3, '&:hover': { backgroundColor: '#fcae4d' } }}
          >
            {sendPaymentLink.isPending ? <CircularProgress size={20} sx={{ color: '#262626' }} /> : "Envoyer l'email"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={giftOpen} onClose={() => setGiftOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Offrir des RDV qualifiés</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14, color: '#464646', mb: 2 }}>
            Un pack gratuit sera crédité immédiatement à l'agence, sans facture. L'agence est prévenue par email
            {agent.partner?.email ? ` (${agent.partner.email})` : ''}. Le motif reste interne.
          </Typography>
          <TextField
            label="Nombre de RDV"
            type="number"
            value={giftSize}
            onChange={(e) => setGiftSize(e.target.value)}
            fullWidth
            inputProps={{ min: 1, step: 1 }}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Motif (interne)"
            value={giftReason}
            onChange={(e) => setGiftReason(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            inputProps={{ maxLength: 255 }}
            placeholder="Ex. : geste commercial suite à un RDV annulé"
          />
          {giftError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {giftError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setGiftOpen(false)} sx={{ color: '#464646', textTransform: 'none' }}>
            Annuler
          </Button>
          <Button
            onClick={submitGift}
            disabled={giftPack.isPending}
            sx={{ backgroundColor: '#1B5E20', color: 'white', textTransform: 'none', fontWeight: 700, px: 3, '&:hover': { backgroundColor: '#164a1a' } }}
          >
            {giftPack.isPending ? <CircularProgress size={20} sx={{ color: 'white' }} /> : `Offrir ${Number(giftSize) || 0} RDV`}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={5000}
        onClose={() => setToast('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setToast('')}>
          {toast}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AdminAgentDetail;
